/* Copyright (c) 2021-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, mergeMap, take } from 'rxjs/operators';
import { BrowserSupportService } from 'browser-support/lib/browser-support.service';
import { DocumentVisibilityState } from 'browser-support/lib/constants';
import { BrowserPlugin } from 'crimson/lib/bridge/browser-plugin/browser-plugin';
import { PluginHandlers, PluginResponse } from 'crimson/lib/bridge/common/plugin.models';
import { FullBridgeCommand } from 'crimson/lib/bridge/bridge.service';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { ServiceWorkerService } from 'service-worker/lib/service-worker.service';
import { ProjectInterfaceService } from 'project-interface/lib/project-interface.service';
import { Commands } from 'bridge-commands/commands';
import { Types as CommandTypes } from 'bridge-commands/types';
import { SwMessageType } from 'service-workers/sw.types';

import Types = CommandTypes.Common.Clientnotification;

const Clientnotification = Commands.Common.Clientnotification;

export enum PushNotificationPermission {
    Granted = 'granted',
    Denied = 'denied',
    Default = 'default'
}

@Injectable({
    providedIn: 'root'
})
export class ClientNotificationPlugin extends BrowserPlugin {
    private logger: Logger;
    private isAllowed: boolean;
    private isDocumentVisible: boolean;
    private success = {
        _return_internal: Types.InternalErrorCodeEnum.Success
    };
    private error = {
        _return_internal: Types.InternalErrorCodeEnum.Error
    };

    constructor(
        loggingService: LoggingService,
        private browserSupportService: BrowserSupportService,
        private serviceWorkerService: ServiceWorkerService,
        private projectInterfaceService: ProjectInterfaceService
    ) {
        super();
        this.logger = loggingService.getLogger('ClientNotificationPlugin');
        this.browserSupportService.documentVisiblityChange.subscribe(_ => {
            this.isDocumentVisible = document.visibilityState === DocumentVisibilityState.Visible;
        });
    }

    public get handlers(): PluginHandlers {
        return {
            module: Clientnotification.ShowClientNotifications.module,
            commands: {
                [Clientnotification.ShowClientNotifications.method]: this.showClientNotification.bind(this),
                [Clientnotification.ClearNotificationsQueue.method]: this.clearNotificationsQueue.bind(this),
                [Clientnotification.DeleteNotification.method]: this.deleteNotification.bind(this)
            }
        };
    }

    private deleteNotification(
        command: FullBridgeCommand<Types.DeleteNotificationParameters>
    ): Observable<PluginResponse> {
        return this.projectInterfaceService.isNotificationAllowedCallBack().pipe(
            catchError(err => this.makeResult(JSON.stringify(this.error))),
            mergeMap((allowed: boolean) => {
                if (allowed) {
                    this.serviceWorkerService.postMessageSw({
                        type: SwMessageType.UiPushNotification,
                        content: JSON.stringify({
                            delete: command.payload.uniqueId
                        })
                    });
                    this.logger.info('Notification Info is sent to SW');
                } else {
                    this.logger.info('Notifications are not allowed');
                }
                return this.makeResult(JSON.stringify(this.success));
            })
        );
    }

    private clearNotificationsQueue(
        command: FullBridgeCommand<Types.ClearNotificationsQueueParameters>
    ): Observable<PluginResponse> {
        return this.projectInterfaceService.isNotificationAllowedCallBack().pipe(
            catchError(err => this.makeResult(JSON.stringify(this.error))),
            mergeMap((allowed: boolean) => {
                if (allowed) {
                    this.serviceWorkerService.postMessageSw({
                        type: SwMessageType.UiPushNotification,
                        content: JSON.stringify({
                            clear: command.payload.clear
                        })
                    });
                }
                return this.makeResult(JSON.stringify(this.success));
            })
        );
    }

    private showClientNotification(
        command: FullBridgeCommand<Types.ShowClientNotificationsParameters>
    ): Observable<PluginResponse> {
        return this.projectInterfaceService.isNotificationAllowedCallBack().pipe(
            catchError(err => this.makeResult(JSON.stringify(this.error))),
            mergeMap((allowed: boolean) => {
                if (!this.canSendNotification(allowed)) {
                    this.logger.info('Cannot send Notification as permission is not given');
                    const errorPermissionDenied = {
                        _return_internal: Types.InternalErrorCodeEnum.Error_PermissionNotGiven
                    };
                    return this.makeResult(JSON.stringify(errorPermissionDenied));
                }
                if (this.isFullScreenAndVisible()) {
                    this.logger.info('Cannot send Notifications when screen is fullscreen & visible');
                    const errorNotFullScreen = {
                        _return_internal: Types.InternalErrorCodeEnum.Error_NotFullScreen
                    };
                    return this.makeResult(JSON.stringify(errorNotFullScreen));
                }
                this.serviceWorkerService.postMessageSw({
                    type: SwMessageType.UiPushNotification,
                    content: JSON.stringify({
                        title: command.payload.title,
                        body: command.payload.body,
                        direction: command.payload.direction,
                        language: command.payload.language,
                        tag: command.payload.tag,
                        silent: command.payload.silent
                    })
                });
                this.logger.info('Notifications are sent to SW');
                // ToDo: Need to return the _return_internal param from other methods in this file as well.
                // will be taken up as a separate task.
                return this.makeResult(String(this.success._return_internal));
            })
        );
    }

    private canSendNotification(allow: boolean): boolean {
        return allow && Notification.permission === PushNotificationPermission.Granted;
    }

    private isFullScreenAndVisible(): boolean {
        return this.browserSupportService.isFullscreen() && this.isDocumentVisible;
    }
}
